import {
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
    type DialogSnapMode,
} from '@plato/ui/containers/dialog';
import { cn } from '@plato/ui/util/classes';
import type { FormEventHandler, PropsWithChildren } from 'react';
import React from 'react';
import type { FieldError, Merge } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { NewContact } from '../../assets/icons/new-contact.tsx';
import { Button } from '../ui/button.tsx';
import { Label } from '../ui/label.tsx';

type IconType = typeof NewContact;

type HeaderProps = {
    title: string | React.ReactNode | React.ReactNode[];
    subtitle?: string | React.ReactNode | React.ReactNode[];
    icon?: IconType;
};

function Header({ title, subtitle, icon: Icon }: HeaderProps) {
    const { t } = useTranslation();

    return (
        <DialogHeader>
            {Icon && <Icon className="size-5 shrink-0" />}
            <div className="flex min-w-0 flex-1 flex-col gap-tight">
                <DialogTitle>{title}</DialogTitle>
                {subtitle && <div className="text-body text-content-weak">{subtitle}</div>}
            </div>
            <DialogIconClose label={t('form__close', { ns: 'common' })} />
        </DialogHeader>
    );
}

type ItemListProps = {
    children: React.ReactNode;
    className?: string;
    grid?: boolean;
    subgrid?: boolean;
    col?: boolean;
};

function ItemList({ children, className, grid, subgrid, col }: ItemListProps) {
    return (
        <div
            className={cn(
                'group flex w-full flex-col gap-3',
                grid && 'is-grid grid grid-cols-[auto_1fr]',
                subgrid && 'is-grid grid grid-cols-subgrid col-span-2',
                col && 'is-col',
                className,
            )}
        >
            {children}
        </div>
    );
}

type GroupProps = {
    children: React.ReactNode;
    className?: string;
};

// Wraps multiple subgrid `ItemList`s so their label columns share the same width.
function Group({ children, className }: GroupProps) {
    return (
        <div className={cn('grid w-full grid-cols-[auto_minmax(0,1fr)] gap-3', className)}>{children}</div>
    );
}

type PanelProps = {
    children: React.ReactNode;
    className?: string;
};

// A rounded white panel that passes the parent `Group`'s columns through to its children via subgrid.
function Panel({ children, className }: PanelProps) {
    return (
        <div
            className={cn(
                'col-span-2 grid grid-cols-subgrid rounded-lg bg-white py-2 px-3 shadow-xs',
                className,
            )}
        >
            {children}
        </div>
    );
}

type ItemProps = {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
    col?: boolean;
};

function Item({ children, className, col }: ItemProps) {
    return (
        <div
            className={cn(
                'flex flex-row gap-3 flex-wrap',
                // GRID STYLE
                'group-[.is-grid]:col-span-2 group-[.is-grid]:grid group-[.is-grid]:grid-cols-subgrid',
                'group-[.is-col]:gap-2 group-[.is-col]:flex-col group-[.is-col]:items-stretch',
                col && 'flex-col items-stretch',
                className,
            )}
        >
            {children}
        </div>
    );
}

type BodyProps = {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
};

function Body({ children, className = '' }: BodyProps) {
    return <div className={cn('flex flex-1', className)}>{children}</div>;
}

type ContentProps = {
    children: React.ReactNode;
    className?: string;
};

// The scrolling band between header and footer. `DialogBody` owns the inset, the
// `min-h-0` that lets it shrink, and the scroll timeline the fold shadows animate on;
// an unprefixed `min-h-*` in className silently replaces that floor (tailwind-merge
// keeps one per group) and the footer gets clipped on short viewports — see PREP-1009.
// Need a height floor? Put it behind a variant, e.g. `[@media(min-height:700px)]:min-h-[500px]`.
function Content({ children, className }: ContentProps) {
    return <DialogBody className={cn('flex flex-col gap-3', className)}>{children}</DialogBody>;
}

type SpacerProps = {
    className?: string;
};

function Spacer({ className = '' }: SpacerProps) {
    return <div className={cn('h-3', className)} />;
}

type ErrorProps = {
    error?: string | FieldError | Merge<FieldError, any>;
};

function Error({ error }: ErrorProps) {
    if (!error) {
        return null;
    }

    return (
        <div className={cn('mt-0.5 flex shrink-0 grow-0 basis-full pl-1 text-sm text-red-600')}>
            {typeof error === 'string' ? error : error.message}
        </div>
    );
}

type SubmitBlockProps = {
    disabled?: boolean;
    isLoading?: boolean;
    onCancel?: () => void;
    onSubmit?: () => void;
    submitText?: string;
    variant?: 'primary' | 'danger';
};

function SubmitBlock({
    onCancel,
    isLoading = false,
    onSubmit,
    submitText,
    disabled,
    variant = 'primary',
}: SubmitBlockProps) {
    const { t } = useTranslation();

    return (
        <DialogFooter>
            {onCancel && (
                <Button type="button" onClick={onCancel} variant="subtle" className="min-w-24">
                    {t('form__cancel', { ns: 'common' })}
                </Button>
            )}
            <Button
                variant={variant}
                isLoading={isLoading}
                disabled={disabled}
                className="min-w-24"
                type={onSubmit ? 'button' : 'submit'}
                onClick={onSubmit}
            >
                {submitText ? submitText : t('form__submit', { ns: 'common' })}
            </Button>
        </DialogFooter>
    );
}

function DialogFormGridItemList({ children }: PropsWithChildren) {
    const gridCols = React.Children.toArray(children).filter((child) => React.isValidElement(child)).length;

    return (
        <div
            className={cn(`grid w-full grid-cols-2 gap-3`, {
                'grid-cols-1': gridCols === 1,
            })}
        >
            {children}
        </div>
    );
}

type DialogFormProps = {
    /** Sizes the card, e.g. `sm:max-w-md`. Widths only — the card's height follows its content. */
    className?: string;
    /** How tall the sheet snaps below `sm`. `max` for a form whose fields render progressively. */
    snapMode?: DialogSnapMode;
    onSubmit?: FormEventHandler<HTMLFormElement>;
} & React.PropsWithChildren;

/**
 * The dialog card, with a `form` spanning its full height so `Header` and `SubmitBlock`
 * stay pinned while `Content` scrolls between them. Goes directly inside `Dialog`.
 */
export function DialogForm({ children, className, snapMode, onSubmit }: DialogFormProps) {
    return (
        <DialogContent className={className} snapMode={snapMode}>
            <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
                {children}
            </form>
        </DialogContent>
    );
}

DialogForm.Header = Header;
DialogForm.ItemList = ItemList;
DialogForm.Item = Item;
DialogForm.Group = Group;
DialogForm.Panel = Panel;
DialogForm.Grid = DialogFormGridItemList;
DialogForm.Label = Label;
DialogForm.Body = Body;
DialogForm.Content = Content;
DialogForm.Footer = DialogFooter;
DialogForm.Spacer = Spacer;
DialogForm.Error = Error;
DialogForm.SubmitBlock = SubmitBlock;
